import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ArrowLeft, ShieldCheck, Download, Package, Plus } from 'lucide-react';
import { Button } from '../components/ui/button';
import Reveal from '../components/Reveal';
import { useQuoteCart } from '../context/QuoteCartContext';
import api, { mediaUrl, asArray } from '../lib/api';
import { FIRE_CLASSES, APPROVALS, COMPANY } from '../mock';

// Normalises an API response into a safe product shape, or null if the payload
// is not a real product object (e.g. a misconfigured backend returned HTML).
function normalizeProduct(data) {
  if (!data || typeof data !== 'object' || Array.isArray(data) || !data.id) return null;
  const variants = asArray(data.variants).filter((v) => v && typeof v === 'object');
  if (variants.length === 0) variants.push({ label: 'Standard', specs: {} });
  const images = asArray(data.images).filter((s) => typeof s === 'string' && s.trim());
  return { ...data, variants, fireClasses: asArray(data.fireClasses), images };
}

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(0);
  const [activeImg, setActiveImg] = useState(0);
  const { addItem } = useQuoteCart();

  useEffect(() => {
    api.get('/certifications').then((r) => setCerts(asArray(r.data))).catch(() => {});
  }, []);

  useEffect(() => {
    setActive(0); setActiveImg(0); setLoading(true); window.scrollTo(0, 0);
    api.get(`/products/${id}`)
      .then((res) => {
        const p = normalizeProduct(res.data);
        setProduct(p);
        if (!p) return null;
        return api.get('/products', { params: { category: p.category } });
      })
      .then((res) => setRelated(asArray(res?.data).filter((p) => p.id !== id).slice(0, 4)))
      .catch(() => setProduct(null))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="bg-[#070a12] min-h-screen grid place-items-center">
        <div className="w-10 h-10 rounded-full border-4 border-secondary border-t-primary animate-spin" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="bg-[#070a12] min-h-screen grid place-items-center">
        <div className="text-center">
          <h1 className="font-heading font-bold text-3xl text-white">Product not found</h1>
          <Button asChild className="mt-6"><Link to="/products">Back to Products</Link></Button>
        </div>
      </div>
    );
  }

  const variant = product.variants[active] || product.variants[0];
  const specs = variant.specs && typeof variant.specs === 'object' ? variant.specs : {};

  // Primary image first, then any additional gallery images (de-duplicated).
  const gallery = [product.image, ...product.images].filter((s, i, arr) => s && arr.indexOf(s) === i);
  const heroImage = gallery[activeImg] || gallery[0] || product.image;

  return (
    <div className="bg-[#070a12]">
      <div className="max-w-7xl mx-auto px-6 pt-10">
        <div className="flex items-center gap-2 text-gray-400 font-heading text-sm flex-wrap">
          <Link to="/" className="hover:text-primary">Home</Link><span>/</span>
          <Link to="/products" className="hover:text-primary">Products</Link><span>/</span>
          <span className="fire-text">{product.name}</span>
        </div>
      </div>

      <section className="py-12">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-14">
          <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7 }} className="relative">
            <div className="rounded-3xl overflow-hidden grad-border">
              <img src={mediaUrl(heroImage)} alt={product.name} className="w-full h-[480px] object-cover" data-testid="product-hero-image" />
            </div>
            {gallery.length > 1 && (
              <div className="flex gap-3 mt-4 flex-wrap" data-testid="product-gallery">
                {gallery.map((g, i) => (
                  <button
                    key={g}
                    type="button"
                    onClick={() => setActiveImg(i)}
                    data-testid={`product-gallery-thumb-${i}`}
                    className={`w-20 h-20 rounded-xl overflow-hidden border-2 transition-all ${i === activeImg ? 'border-primary scale-105' : 'border-white/10 hover:border-white/30'}`}
                  >
                    <img src={mediaUrl(g)} alt={`${product.name} view ${i + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
            <div className="orb w-[300px] h-[300px] bg-primary/20 -z-0 -bottom-10 -left-10 animate-glow" />
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7, delay: 0.1 }}>
            <span className="text-xs font-semibold fire-text uppercase tracking-wider">{product.category}</span>
            <h1 className="font-heading font-extrabold text-4xl md:text-5xl text-white mt-2" data-testid="product-title">{product.name}</h1>
            <p className="text-gray-400 text-lg mt-5 leading-relaxed">{product.long}</p>

            <div className="mt-7">
              <p className="section-eyebrow text-gray-500 mb-3 flex items-center gap-2"><Package className="w-4 h-4 text-primary" /> Select Variant / SKU</p>
              <div className="flex flex-wrap gap-2">
                {product.variants.map((v, i) => (
                  <button key={v.label} data-testid={`sku-${i}`} onClick={() => setActive(i)}
                    className={`px-4 py-2 rounded-xl text-sm font-heading font-semibold transition-all ${i === active ? 'bg-gradient-to-r from-primary to-accent text-white shadow-lg shadow-primary/30 scale-105' : 'glass text-gray-300 hover:text-white'}`}>
                    {v.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-7">
              <p className="section-eyebrow text-gray-500 mb-3">Specification {'\u2014'} <span className="fire-text">{variant.label}</span></p>
              <AnimatePresence mode="wait">
                <motion.div key={variant.label} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.35 }}
                  className="glass rounded-2xl overflow-hidden" data-testid="product-specs">
                  {Object.entries(specs).map(([k, val], i) => (
                    <div key={k} className={`flex justify-between gap-4 px-5 py-3 ${i % 2 ? 'bg-white/[0.02]' : ''}`}>
                      <span className="text-xs text-gray-500 font-heading font-semibold uppercase tracking-wide">{k}</span>
                      <span className="text-sm text-white font-semibold text-right">{val}</span>
                    </div>
                  ))}
                </motion.div>
              </AnimatePresence>
            </div>

            {product.fireClasses?.length > 0 && (
              <div className="mt-7">
                <p className="section-eyebrow text-gray-500 mb-3">Suitable for Class of Fire</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  {product.fireClasses.map((c) => (
                    <div key={c} className="flex items-center gap-3 glass rounded-xl p-3">
                      <span className="grid place-items-center w-10 h-10 rounded-lg bg-gradient-to-br from-primary to-accent text-white font-display font-extrabold text-lg">{c}</span>
                      <span className="text-sm text-gray-300">{FIRE_CLASSES[c]}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-3 mt-9">
              <Button data-testid="add-to-enquiry-btn" onClick={() => addItem({ productId: product.id, name: product.name, image: product.image, sku: variant.label })} size="lg" className="gap-2 bg-gradient-to-r from-primary to-accent hover:opacity-90 shadow-lg shadow-primary/30">Add to Enquiry <Plus className="w-4 h-4" /></Button>
              <Button asChild variant="outline" size="lg" className="gap-2 bg-white/5 border-white/15 text-white hover:bg-white/10"><Link to="/contact">Get a Quote <ArrowRight className="w-4 h-4" /></Link></Button>
              <Button asChild variant="ghost" size="lg" className="gap-2 text-gray-300 hover:text-white"><a href={COMPANY.brochureUrl} target="_blank" rel="noopener noreferrer"><Download className="w-4 h-4" /> Brochure</a></Button>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6">
          <p className="text-center section-eyebrow text-gray-500 justify-center mb-8">Approvals</p>
          <div className="flex flex-wrap items-center justify-center gap-8">
            {(certs.length ? certs : APPROVALS.map((a) => ({ name: a }))).map((c) => (
              c.logo
                ? <img key={c.name} src={mediaUrl(c.logo)} alt={c.name} className="h-20 w-auto object-contain opacity-80 grayscale transition duration-300 hover:opacity-100 hover:grayscale-0 hover:scale-110 hover:drop-shadow-[0_0_16px_rgba(232,51,42,0.5)]" />
                : <div key={c.name} className="font-display font-bold text-xl text-white/90 flex items-center gap-2.5"><ShieldCheck className="w-5 h-5 text-primary" /> {c.name}</div>
            ))}
          </div>
        </div>
      </section>

      {related.length > 0 && (
        <section className="py-16 bg-[#05070d]">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex items-center justify-between mb-10">
              <h2 className="font-heading font-extrabold text-3xl text-white">Related Products</h2>
              <Button asChild variant="ghost" className="text-gray-300 hover:text-white gap-2"><Link to="/products"><ArrowLeft className="w-4 h-4" /> All Products</Link></Button>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {related.map((p) => (
                <Link key={p.id} to={`/product/${p.id}`} className="group glass glass-hover rounded-3xl overflow-hidden">
                  <div className="img-zoom h-44"><img src={mediaUrl(p.image)} alt={p.name} className="w-full h-full object-cover" /></div>
                  <div className="p-5"><h3 className="font-heading font-bold text-white group-hover:fire-text transition-all">{p.name}</h3></div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
